import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { applyTenantRequestHeaders } from '../../lib/tenantRequestContext';
import { getSessionForAuthenticatedRequest, onSessionChange } from '../../lib/auth';
import {
  DEFAULT_DISPLAY_TIMEZONE_PREFERENCE,
  normalizeDisplayTimezonePreference,
  resolveDisplayTimezone,
  type DisplayTimezonePreference,
} from '../../lib/time/displayTimezone';

type DisplayTimezoneContextValue = {
  preference: DisplayTimezonePreference;
  timeZone: string;
  loading: boolean;
  savePreference: (preference: DisplayTimezonePreference) => Promise<void>;
};

const DisplayTimezoneContext = createContext<DisplayTimezoneContextValue | null>(null);
const PREFERENCE_KEY = 'display-timezone-v1';

export function DisplayTimezoneProvider({ children }: { children: ReactNode }) {
  const [preference, setPreference] = useState<DisplayTimezonePreference>(DEFAULT_DISPLAY_TIMEZONE_PREFERENCE);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);

  const loadPreference = useCallback(async () => {
    const requestGeneration = ++generation.current;
    setPreference(DEFAULT_DISPLAY_TIMEZONE_PREFERENCE);
    setLoading(true);
    try {
      const session = await getSessionForAuthenticatedRequest({ reason: 'display_timezone_preference' });
      if (requestGeneration !== generation.current || !session?.user?.id || !session.access_token) return;
      const response = await fetch(`/api/user-preferences/${PREFERENCE_KEY}`, {
        headers: applyTenantRequestHeaders({ Authorization: `Bearer ${session.access_token}` }),
      });
      if (!response.ok) throw new Error(`Display timezone preference unavailable (${response.status})`);
      const result = await response.json();
      if (requestGeneration !== generation.current) return;
      setPreference(normalizeDisplayTimezonePreference(result?.value));
    } catch {
      if (requestGeneration === generation.current) setPreference(DEFAULT_DISPLAY_TIMEZONE_PREFERENCE);
    } finally {
      if (requestGeneration === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPreference();
    const unsubscribe = onSessionChange(() => { void loadPreference(); });
    const onTenantChanged = () => { void loadPreference(); };
    window.addEventListener('trade-proof-tenant-context-changed', onTenantChanged);
    return () => {
      generation.current += 1;
      unsubscribe?.();
      window.removeEventListener('trade-proof-tenant-context-changed', onTenantChanged);
    };
  }, [loadPreference]);

  const savePreference = useCallback(async (nextPreference: DisplayTimezonePreference) => {
    const operationGeneration = generation.current;
    const normalized = normalizeDisplayTimezonePreference(nextPreference);
    const session = await getSessionForAuthenticatedRequest({ reason: 'save_display_timezone_preference' });
    if (operationGeneration !== generation.current) throw new Error('Authentication changed; timezone preference was not applied.');
    if (!session?.user?.id || !session.access_token) throw new Error('Sign in to save your display timezone preference.');
    const response = await fetch(`/api/user-preferences/${PREFERENCE_KEY}`, {
      method: 'PUT',
      headers: applyTenantRequestHeaders({ Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }),
      body: JSON.stringify(normalized),
    });
    if (!response.ok) throw new Error('Could not save the display timezone preference.');
    if (operationGeneration !== generation.current) throw new Error('Authentication changed; timezone preference was not applied.');
    setPreference(normalized);
  }, []);

  const value = useMemo(() => ({
    preference,
    timeZone: resolveDisplayTimezone(preference),
    loading,
    savePreference,
  }), [preference, loading, savePreference]);

  return <DisplayTimezoneContext.Provider value={value}>{children}</DisplayTimezoneContext.Provider>;
}

export function useDisplayTimezonePreference(): DisplayTimezoneContextValue {
  const context = useContext(DisplayTimezoneContext);
  if (context) return context;
  return {
    preference: DEFAULT_DISPLAY_TIMEZONE_PREFERENCE,
    timeZone: resolveDisplayTimezone(DEFAULT_DISPLAY_TIMEZONE_PREFERENCE),
    loading: false,
    savePreference: async () => { throw new Error('Timezone settings are unavailable outside the authenticated app.'); },
  };
}

export function useDisplayTimezone(): string {
  return useDisplayTimezonePreference().timeZone;
}
